import * as React from 'react'

import { cn } from '@/lib/utils'

export interface RevealProps {
  children: React.ReactNode
  /** Stagger delay in ms — use to sequence multiple Reveal siblings a beat apart. */
  delay?: number
  className?: string
}

/**
 * Scroll-triggered entrance: fades and slides content up into place the
 * first time it crosses into the viewport. One consistent timing/easing
 * for the whole site — don't give individual sections their own curve or
 * duration, that consistency is the point.
 *
 * `m-reveal` / `reveal-in-view` carry no styles — they're hooks for
 * marketing.css (hero timing overrides) and the eyebrow-highlight
 * animation in index.css. (`m-` prefix because the app owns a global
 * `.reveal` with different timing.)
 */
export function Reveal({ children, delay = 0, className }: RevealProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [inView, setInView] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return

    // IntersectionObserver's first callback can read a layout that hasn't
    // settled yet (fonts/images still loading), so already-in-view content
    // — the hero, above the fold — can get a stale "not intersecting" first
    // read with nothing afterward to trigger a recheck. A manual rect check
    // on mount covers that case directly, instead of waiting on the observer.
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
      setInView(true)
      return
    }

    // threshold is a fraction of the TARGET's own area, which breaks down for
    // content taller than ~5x the viewport (on mobile especially): the
    // viewport can never cover 20% of something that tall, so isIntersecting
    // never fires and the section stays invisible. rootMargin shrinks the
    // effective viewport instead, so a threshold of 0 fires as soon as the
    // element's top edge crosses 80% down the viewport — matching the
    // on-mount check above, regardless of the element's height.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0, rootMargin: '0px 0px -20% 0px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        'm-reveal translate-y-6 opacity-0 transition-[opacity,transform] duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
        inView && 'reveal-in-view translate-y-0 opacity-100',
        className
      )}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}
